import { Component, Event, EventEmitter, h, Host, Prop } from "@stencil/core";
import { SwitchLabelPlacement } from "./switch-input-types";

@Component({
    tag: "ux-switch-input",
    styleUrl: "switch-input.scss",
    shadow: true
})
export class SwitchInput {
    /**
     * Indicates if the switch is disabled.
     * The switch will be in a non-interactive state if this is set to true.
     * @default false
     */
    @Prop() readonly disabled: boolean = false;

    /**
     * Determines whether the switch is checked or not.
     * This is a mutable property that can be changed programmatically.
     * Reflects the state to the component's attribute for CSS styling.
     * @default false
     */
    @Prop({ mutable: true, reflect: true }) checked = false;

    /**
     * The name of the switch component.
     * Can be used to identify the component when used in forms or scripting.
     */
    @Prop() readonly name: string;

    /**
     * The label associated with the switch component.
     * Provides a textual label for screen readers and for better accessibility.
     */
    @Prop() readonly label: string;

    /**
     * A description for the switch component.
     * This can be additional information that is not included in the label.
     */
    @Prop() readonly desc: string;

    /**
     * The position of the label relative to the switch.
     * Can be 'start' or 'end', which places the label before or after the switch.
     * @default SwitchLabelPlacement.Start
     */
    @Prop() readonly labelPlacement: SwitchLabelPlacement = SwitchLabelPlacement.Start;

    /**
     * Event fired when the switch's on/off state changes.
     * This event is composed, which means it will bubble through the shadow DOM boundary.
     * It is also cancelable, so the default action can be prevented.
     * When the event is dispatched, it bubbles up through the DOM tree.
     *
     * The event's detail is the new boolean value of the switch's state.
     *
     * @event uxChange
     */
    @Event({
        eventName: "uxChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    didToggle: EventEmitter<boolean>;

    componentWillLoad() {
        if (!this.label) {
            // eslint-disable-next-line no-console
            console.warn("Accessibility error: 'label' property is required for screen reader identification.");
        }
    }

    private toggle = (e: UIEvent): void => {
        e.preventDefault();
        const { disabled } = this;
        if (disabled) {
            return;
        }
        const on = !this.checked;
        const toggleEvent: CustomEvent = this.didToggle.emit(on);
        if (toggleEvent.defaultPrevented) {
            return;
        }
        this.checked = on;
    };

    render() {
        return (
            <Host checked={this.checked} disabled={this.disabled} labelPlacement={this.labelPlacement}>
                <label>
                    {this.label && <span id="inner-label">{this.label}</span>}
                    <button
                        role="switch"
                        aria-checked={this.checked ? "true" : "false"}
                        tabIndex={this.disabled ? -1 : 0}
                        type="button"
                        disabled={this.disabled}
                        onClick={this.toggle}
                    >
                        <span id="handle" />
                    </button>
                </label>
                {this.desc && <div id="description">{this.desc}</div>}
            </Host>
        );
    }
}
